import { User, UserManager } from "oidc-react";
import { WebStorageStateStore } from "oidc-client-ts";
import { getAuthConfig, getRuntimeConfig } from "../runtimeConfig";

let auth0Manager: UserManager | null = null;

/**
 * Get or create the Auth0 UserManager instance
 */
const getAuth0Manager = (): UserManager => {
  if (!auth0Manager) {
    const config = getAuthConfig();
    // Get the base path from runtime config, default to empty string if not set or "/"
    let basePath = "";
    try {
      basePath = getRuntimeConfig().api?.basePath || "";
    } catch {
      basePath = "";
    }
    if (basePath && basePath !== "/") {
      // Ensure basePath starts with / and does not end with /
      basePath = basePath.startsWith("/") ? basePath : "/" + basePath;
      basePath = basePath.endsWith("/") ? basePath.slice(0, -1) : basePath;
    } else {
      basePath = "";
    }
    const redirectUri = window.location.origin + basePath;

    auth0Manager = new UserManager({
      authority: config.authority,
      client_id: config.clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: config.scopes,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: true,
      silent_redirect_uri: redirectUri,
      metadata: {
        issuer: config.authority,
        authorization_endpoint: `${config.authority}/authorize`,
        token_endpoint: `${config.authority}/oauth/token`,
        userinfo_endpoint: `${config.authority}/userinfo`,
        jwks_uri: `${config.authority}/.well-known/jwks.json`,
        end_session_endpoint: `${config.authority}/oidc/logout`,
      },
      extraQueryParams: {
        audience: config.audience, // Add audience to request JWT tokens instead of opaque tokens
      },
    });

    auth0Manager.events.addSilentRenewError((error) => {
      console.error("Auth0 silent renew error:", error);

      // Handle token expiration silently in the background
      // Clear stored user data to force re-authentication on next request
      const manager = getAuth0Manager();
      manager.clearStaleState().catch(console.error);
      manager.removeUser().catch(console.error);

      // Clean up any redirect tracking
      localStorage.removeItem("auth0-last-redirect");

      console.debug("Token expired - user will be prompted to re-authenticate on next request");
    });
  }

  return auth0Manager;
};

export const auth0Config = {
  onBeforeSignIn: () => {
    const initialRef = window.location.href;
    window.localStorage.setItem("Auth0InitialRef", initialRef);
    return initialRef;
  },
  onSignIn: (user: User | null) => {
    const initialRef = window.localStorage.getItem("Auth0InitialRef");
    if (initialRef) {
      window.location.href = initialRef;
    }
  },
  userManager: getAuth0Manager(),
  location: window.location,
};

export default getAuth0Manager();

export const getTokenAsync = async () => {
  const manager = getAuth0Manager();
  const user = await manager.getUser();
  return user?.access_token;
};

export const logout = async () => {
  const manager = getAuth0Manager();
  await manager.signoutRedirect();
};

export const getUserName = () => {
  // This would need to be implemented to get the user name from Auth0
  return "Auth0 User";
};
